// Executive summary: the headline profit & loss and balance sheet numbers on
// one page with period-over-period changes. Composed client-side from the
// existing comparative P&L and balance sheet endpoints — no new endpoint.
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import {
  type FinanceStatementResponse,
  getBalanceSheet,
  getProfitAndLoss,
} from "@/lib/api";
import { pnlHeadline } from "./pnl-summary";
import { previousRange, sameDayLastYear } from "./report-dates";
import { computeChange, formatPercent } from "./variance";

const DISPLAY_CURRENCY = "USD";

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function money(cents: number | null): string {
  if (cents === null) return "—";
  return formatMoney({ cents, currency: DISPLAY_CURRENCY });
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function firstOfMonthIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
}

/** One balance-sheet section subtotal for a comparative column. */
function sectionTotal(
  statement: FinanceStatementResponse,
  label: string,
  column: number,
): number | null {
  const section = (statement.sections ?? []).find(
    (item) => item.label.toLowerCase() === label.toLowerCase(),
  );
  if (!section) return null;
  return section.subtotals_cents?.[column] ?? section.subtotal_cents ?? null;
}

function SummaryRow({
  label,
  current,
  previous,
  favorableWhenUp,
}: {
  label: string;
  current: number | null;
  previous: number | null;
  favorableWhenUp: boolean;
}) {
  const change = computeChange(current, previous, { favorableWhenUp });
  const tone =
    change === null || change.favorable === null
      ? "text-[var(--muted-foreground)]"
      : change.favorable
        ? "text-[var(--status-success-foreground)]"
        : "text-[var(--danger)]";
  return (
    <TableRow>
      <TableCell className="font-medium">{label}</TableCell>
      <TableCell className="text-right tabular-nums">{money(current)}</TableCell>
      <TableCell className="text-right tabular-nums">{money(previous)}</TableCell>
      <TableCell className={`text-right tabular-nums ${tone}`}>
        {change
          ? `${change.absoluteCents > 0 ? "+" : ""}${money(change.absoluteCents)}`
          : "—"}
      </TableCell>
      <TableCell className={`text-right tabular-nums ${tone}`}>
        {change ? formatPercent(change.percent) : "—"}
      </TableCell>
    </TableRow>
  );
}

export function ExecutiveSummaryPage() {
  const [pl, setPl] = useState<FinanceStatementResponse | null>(null);
  const [bs, setBs] = useState<FinanceStatementResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    const start = firstOfMonthIso();
    const end = todayIso();
    const prior = previousRange(start, end);
    const asAt = end;
    const lastYear = sameDayLastYear(asAt);
    setLoading(true);
    setError(null);
    Promise.all([
      getProfitAndLoss({
        start_date: start,
        end_date: end,
        ...(prior ? { compare_from: prior.start, compare_to: prior.end } : {}),
      }),
      getBalanceSheet({
        as_at: asAt,
        ...(lastYear ? { compare_date: lastYear } : {}),
      }),
    ])
      .then(([plStatement, bsStatement]) => {
        setPl(plStatement);
        setBs(bsStatement);
      })
      .catch((cause: unknown) => setError(toErrorMessage(cause)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const current = pl ? pnlHeadline(pl, 0) : null;
  const previous = pl ? pnlHeadline(pl, 1) : null;

  return (
    <div className="grid gap-6">
      <PageHeader
        title="Executive summary"
        description="This month's revenue, expenses, and profit beside the balance sheet as at today — each with its change."
        actions={
          <Button asChild variant="secondary">
            <Link to="/reports">All reports</Link>
          </Button>
        }
      />

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          <div className="mt-3">
            <Button type="button" variant="secondary" onClick={load}>
              Retry
            </Button>
          </div>
        </div>
      ) : null}

      {loading && !pl && !bs && !error ? (
        <div className="surface rounded-2xl p-5 text-sm text-[var(--muted-foreground)]">
          Loading…
        </div>
      ) : pl && bs && current ? (
        <>
          <section aria-label="Profit and loss" className="grid gap-3">
            <h2 className="text-lg font-semibold tracking-tight">
              This month vs the previous period
            </h2>
            <div className="surface overflow-hidden rounded-2xl">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Metric</TableHead>
                    <TableHead className="text-right">This period</TableHead>
                    <TableHead className="text-right">Previous</TableHead>
                    <TableHead className="text-right">Change</TableHead>
                    <TableHead className="text-right">Change %</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <SummaryRow
                    label="Income"
                    current={current.revenueCents}
                    previous={previous?.revenueCents ?? null}
                    favorableWhenUp
                  />
                  <SummaryRow
                    label="Expenses"
                    current={current.expensesCents}
                    previous={previous?.expensesCents ?? null}
                    favorableWhenUp={false}
                  />
                  <SummaryRow
                    label="Net income"
                    current={current.netIncomeCents}
                    previous={previous?.netIncomeCents ?? null}
                    favorableWhenUp
                  />
                </TableBody>
              </Table>
            </div>
          </section>

          <section aria-label="Balance sheet" className="grid gap-3">
            <h2 className="text-lg font-semibold tracking-tight">
              What you own and owe
            </h2>
            <div className="surface overflow-hidden rounded-2xl">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Metric</TableHead>
                    <TableHead className="text-right">As at today</TableHead>
                    <TableHead className="text-right">A year ago</TableHead>
                    <TableHead className="text-right">Change</TableHead>
                    <TableHead className="text-right">Change %</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <SummaryRow
                    label="Assets"
                    current={sectionTotal(bs, "Assets", 0)}
                    previous={sectionTotal(bs, "Assets", 1)}
                    favorableWhenUp
                  />
                  <SummaryRow
                    label="Liabilities"
                    current={sectionTotal(bs, "Liabilities", 0)}
                    previous={sectionTotal(bs, "Liabilities", 1)}
                    favorableWhenUp={false}
                  />
                  <SummaryRow
                    label="Equity"
                    current={sectionTotal(bs, "Equity", 0)}
                    previous={sectionTotal(bs, "Equity", 1)}
                    favorableWhenUp
                  />
                </TableBody>
              </Table>
            </div>
          </section>
        </>
      ) : !error ? (
        <div className="surface rounded-2xl p-5 text-sm text-[var(--muted-foreground)]">
          No statement data is available yet.
        </div>
      ) : null}
    </div>
  );
}
